// 推送通知工具类
export class PushNotificationService {
  private static instance: PushNotificationService;
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  private constructor() {}

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  // 检查浏览器是否支持推送
  public isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // 检查是否支持通知
  public isNotificationSupported(): boolean {
    return 'Notification' in window;
  }

  // 注册Service Worker
  public async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    try {
      if (!this.isSupported()) {
        throw new Error('浏览器不支持推送功能');
      }

      console.log('开始注册Service Worker...');
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      console.log('Service Worker注册成功:', this.registration);
      
      // 等待Service Worker激活
      if (this.registration.installing) {
        console.log('Service Worker正在安装...');
        await new Promise<void>((resolve) => {
          this.registration!.installing!.addEventListener('statechange', () => {
            if (this.registration!.installing!.state === 'installed') {
              console.log('Service Worker安装完成');
              resolve();
            }
          });
        });
      }
      
      // 监听Service Worker更新
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration!.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('新的Service Worker已安装，需要刷新页面');
            }
          });
        }
      });

      return this.registration;
    } catch (error) {
      console.error('Service Worker注册失败:', error);
      throw error;
    }
  }

  // 请求通知权限
  public async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!this.isNotificationSupported()) {
      throw new Error('浏览器不支持通知功能');
    }

    // 检查当前权限状态
    if (Notification.permission === 'granted') {
      return Notification.permission;
    }

    if (Notification.permission === 'denied') {
      throw new Error('通知权限已被拒绝，请在浏览器设置中手动开启');
    }

    // 请求权限
    const permission = await Notification.requestPermission();
    console.log('通知权限状态:', permission);
    return permission;
  }

  // 获取VAPID公钥
  private async getVapidPublicKey(): Promise<string> {
    try {
      // 尝试从后端获取VAPID公钥
      const response = await fetch('/api/vapid-public-key');
      if (response.ok) {
        const data = await response.json();
        return data.publicKey;
      }
    } catch (error) {
      console.warn('无法从后端获取VAPID公钥，使用默认值:', error);
    }

    // 如果无法从后端获取，使用一个测试用的公钥
    // 注意：这是一个示例公钥，实际使用时应该从后端获取
    return 'BEl62iUYgUivxIkv69yViEuiBIa1HI0FZfS0P4wYG8nUZFE3HfRmvXq3oEj8woMoAqo7QfMzm8c2fL2CeI6RXw';
  }

  // 订阅推送服务
  public async subscribeToPush(): Promise<PushSubscription | null> {
    try {
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      // 请求通知权限
      const permission = await this.requestNotificationPermission();
      if (permission !== 'granted') {
        throw new Error('通知权限未授予');
      }

      // 检查是否已经订阅
      const existingSubscription = await this.registration!.pushManager.getSubscription();
      if (existingSubscription) {
        this.subscription = existingSubscription;
        console.log('已存在推送订阅:', existingSubscription);
        return existingSubscription;
      }

      // 获取VAPID公钥
      const vapidPublicKey = await this.getVapidPublicKey();
      console.log('使用VAPID公钥:', vapidPublicKey);
      
      // 转换VAPID公钥格式
      const convertedVapidKey = this.urlBase64ToUint8Array(vapidPublicKey);
      console.log('转换后的VAPID公钥:', convertedVapidKey);

      // 创建新的订阅
      this.subscription = await this.registration!.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });

      console.log('推送订阅成功:', this.subscription);
      return this.subscription;
    } catch (error) {
      console.error('推送订阅失败:', error);
      throw error;
    }
  }

  // 取消订阅
  public async unsubscribeFromPush(): Promise<boolean> {
    try {
      if (!this.subscription) {
        console.log('没有活跃的推送订阅');
        return true;
      }

      const result = await this.subscription.unsubscribe();
      this.subscription = null;
      console.log('推送订阅已取消');
      return result;
    } catch (error) {
      console.error('取消推送订阅失败:', error);
      throw error;
    }
  }

  // 获取当前订阅
  public async getCurrentSubscription(): Promise<PushSubscription | null> {
    try {
      if (!this.registration) {
        await this.registerServiceWorker();
      }
      return await this.registration!.pushManager.getSubscription();
    } catch (error) {
      console.error('获取当前订阅失败:', error);
      return null;
    }
  }

  // 发送测试推送（仅用于开发测试）
  public async sendTestPush(): Promise<void> {
    try {
      if (!this.subscription) {
        throw new Error('没有活跃的推送订阅');
      }

      // 获取认证令牌
      const token = localStorage.getItem('authToken');
      
      // 调用后端API发送真正的推送
      const subscriptionInfo = this.getSubscriptionInfo();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/push/test', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          subscription: subscriptionInfo,
          title: 'Delta测试推送',
          body: '这是一条测试推送消息',
          icon: '/delta.png',
          data: {
            url: '/',
            timestamp: Date.now(),
            type: 'test-push'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '发送推送失败');
      }

      const result = await response.json();
      console.log('测试推送发送成功:', result);
    } catch (error) {
      console.error('发送测试推送失败:', error);
      throw error;
    }
  }

  // 通过Service Worker发送本地通知
  public async sendLocalNotification(title: string, options?: NotificationOptions): Promise<void> {
    try {
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      // 通过消息传递让Service Worker显示通知
      const messageChannel = new MessageChannel();
      
      return new Promise((resolve, reject) => {
        messageChannel.port1.onmessage = (event) => {
          if (event.data.error) {
            reject(new Error(event.data.error));
          } else {
            resolve();
          }
        };

        this.registration!.active?.postMessage({
          type: 'SHOW_NOTIFICATION',
          data: {
            title,
            options: {
              body: '本地通知',
              icon: '/delta.png',
              badge: '/delta.png',
              tag: 'local-notification',
              data: {
                url: '/',
                timestamp: Date.now(),
                type: 'local'
              },
              ...options
            }
          }
        }, [messageChannel.port2]);
      });
    } catch (error) {
      console.error('发送本地通知失败:', error);
      throw error;
    }
  }

  // 测试Service Worker连接
  public async testServiceWorkerConnection(): Promise<boolean> {
    try {
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      const messageChannel = new MessageChannel();
      
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          resolve(false);
        }, 5000);

        messageChannel.port1.onmessage = (event) => {
          clearTimeout(timeout);
          if (event.data.type === 'PONG') {
            console.log('Service Worker连接正常');
            resolve(true);
          } else {
            resolve(false);
          }
        };

        this.registration!.active?.postMessage({
          type: 'PING'
        }, [messageChannel.port2]);
      });
    } catch (error) {
      console.error('Service Worker连接测试失败:', error);
      return false;
    }
  }

  // 显示本地通知（用于测试）
  public async showLocalNotification(title: string, options?: NotificationOptions): Promise<void> {
    if (!this.isNotificationSupported()) {
      throw new Error('浏览器不支持通知功能');
    }

    if (Notification.permission !== 'granted') {
      throw new Error('通知权限未授予');
    }

    const defaultOptions: NotificationOptions = {
      body: '你有一个新的通知',
      icon: '/delta.png',
      badge: '/delta.png',
      tag: 'delta-notification',
      requireInteraction: true,
      ...options
    };

    const notification = new Notification(title, defaultOptions);
    
    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return new Promise((resolve) => {
      notification.onshow = () => resolve();
    });
  }

  // 工具方法：将VAPID公钥转换为Uint8Array
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    // 处理URL安全的Base64格式（包含-和_字符）
    // 将URL安全的Base64转换为标准Base64
    const base64 = base64String
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // 添加填充字符
    const padding = '='.repeat((4 - base64.length % 4) % 4);
    const paddedBase64 = base64 + padding;

    const rawData = window.atob(paddedBase64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // 获取订阅信息用于发送到后端
  public getSubscriptionInfo(): any {
    if (!this.subscription) {
      return null;
    }

    return {
      endpoint: this.subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(this.subscription.getKey('p256dh')!),
        auth: this.arrayBufferToBase64(this.subscription.getKey('auth')!)
      }
    };
  }

  // 工具方法：将ArrayBuffer转换为Base64
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

// 导出单例实例
export const pushNotificationService = PushNotificationService.getInstance();
